/**
 * Expense Dialog Components
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { PoundSterling } from 'lucide-react';
import { CATEGORIES, REIMBURSEMENT_STATUS } from './config';

export const ExpenseDialog = ({ 
  open, 
  onOpenChange, 
  editingExpense, 
  formData, 
  setFormData, 
  onSave, 
  saving 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSave} className="space-y-4">
          {/* Date & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                required
                data-testid="expense-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="expense-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {Object.entries(CATEGORIES).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <config.icon className={`h-4 w-4 ${config.color}`} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label className="text-slate-300">Description *</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Flight to Aberdeen for DLV2000"
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              required
              data-testid="expense-description-input"
            />
          </div>
          
          {/* Amount */}
          <div className="space-y-2">
            <Label className="text-slate-300">Amount (£) *</Label>
            <div className="relative">
              <PoundSterling className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="bg-slate-900 border-slate-600 text-white pl-9"
                min="0"
                step="0.01"
                required
                data-testid="expense-amount-input"
              />
            </div>
          </div>
          
          {/* Reimbursement Status */}
          <div className="space-y-2">
            <Label className="text-slate-300">Reimbursement Status</Label>
            <Select 
              value={formData.reimbursement_status} 
              onValueChange={(value) => setFormData({ ...formData, reimbursement_status: value })}
            >
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="expense-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {Object.entries(REIMBURSEMENT_STATUS).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">
                    <div className="flex items-center gap-2">
                      <config.icon className={`h-4 w-4 ${config.color}`} />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Tax Deductible Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700">
            <div>
              <p className="font-medium text-white">Tax Deductible</p>
              <p className="text-sm text-slate-400">Can this expense be claimed for tax relief?</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_tax_deductible: !formData.is_tax_deductible })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.is_tax_deductible ? 'bg-amber-600' : 'bg-slate-600'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.is_tax_deductible ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-slate-300">Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional details..."
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 min-h-[80px]"
              data-testid="expense-notes-input"
            />
          </div>
          
          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-expense-btn">
              {saving ? 'Saving...' : (editingExpense ? 'Update Expense' : 'Add Expense')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteConfirmDialog = ({ 
  open, 
  onOpenChange, 
  expense, 
  onDelete, 
  deleting 
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-red-400">
            Delete Expense
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-slate-300">
            Are you sure you want to delete this expense?
          </p>
          {expense && (
            <div className="mt-3 p-3 bg-slate-900 rounded-lg border border-slate-700">
              <p className="font-medium text-white">{expense.description}</p>
              <p className="text-sm text-slate-400 mt-1">
                £{expense.amount?.toLocaleString()} • {CATEGORIES[expense.category]?.label || expense.category}
              </p>
            </div>
          )}
          <p className="text-sm text-slate-500 mt-3">
            This action cannot be undone.
          </p>
        </div>
        
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300" disabled={deleting}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onDelete(expense?.id)} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
            {deleting ? 'Deleting...' : 'Delete Expense'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
